/**
 * CalendarEventModal - Modal premium de detalhes de atividade do calendário
 * Layout alinhado ao modal de anotações do chat (capa premium + conteúdo + footer fixo).
 *
 * @version 1.3.0
 * RELEASE: 2026-06-25 v1.3.0 - Layout estilo ConversationContactNotesModal (capa
 *          premium com ícone, corpo rolável, footer fixo). Avatar real do responsável
 *          via shadcn Avatar, bolinha de cor antes do nome do calendário e botão
 *          "Concluir Agendamento" em verde (token --success).
 * RELEASE: 2026-06-25 v1.2.0 - Redesign premium: chip de data/hora, grid de metadados.
 * RELEASE: 2026-02-20 v1.1.0 - Adicionado botão "Cancelar Agendamento" com AlertDialog
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  Phone,
  Users,
  Mail,
  CheckSquare,
  FileText,
  Calendar,
  User,
  Building2,
  Check,
  RotateCcw,
  ExternalLink,
  Loader2,
  Video,
  XCircle,
  Ban,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ACTIVITY_LABELS } from '@/hooks/useCalendarActivities';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize';

// Ícones por tipo
const TYPE_ICONS: Record<string, React.ReactNode> = {
  call: <Phone className="h-4 w-4" />,
  meeting: <Users className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  task: <CheckSquare className="h-4 w-4" />,
  note: <FileText className="h-4 w-4" />,
};

// Ícones grandes (capa premium do header)
const TYPE_ICONS_LG: Record<string, React.ReactNode> = {
  call: <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />,
  meeting: <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />,
  email: <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />,
  task: <CheckSquare className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />,
  note: <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />,
};

interface CalendarEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: {
    activity: {
      id: string;
      title: string;
      description: string | null;
      type: string;
      scheduled_at: string;
      completed_at: string | null;
    };
    type: string;
    isCompleted: boolean;
    dealId: string | null;
    dealTitle?: string;
    customerName?: string;
    description?: string;
    conferencingLink?: string | null;
    bookingCancelToken?: string | null;
    bookingStatus?: string | null;
    hostUserName?: string | null;
    /** v1.3.0 — avatar URL do responsável (profiles.avatar_url) */
    hostAvatarUrl?: string | null;
    calendarEmail?: string | null;
    /** v1.3.0 — cor do calendário/event_type para bolinha indicadora */
    calendarColor?: string | null;
    /** v1.4.0 — nome do dono do calendário (distingue "Pessoal" entre agentes) */
    calendarOwnerName?: string | null;
  } | null;
  onComplete: (activityId: string) => void;
  onReopen: (activityId: string) => void;
  onOpenDeal: (dealId: string) => void;
  onCancelBooking?: (cancelToken: string, reason?: string) => void;
  isCompleting?: boolean;
  isReopening?: boolean;
  isCancelling?: boolean;
}

export function CalendarEventModal({
  open,
  onOpenChange,
  event,
  onComplete,
  onReopen,
  onOpenDeal,
  onCancelBooking,
  isCompleting = false,
  isReopening = false,
  isCancelling = false,
}: CalendarEventModalProps) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const bookingStatus = event?.bookingStatus;
  React.useEffect(() => {
    if (bookingStatus === 'cancelled' && showCancelConfirm) {
      setShowCancelConfirm(false);
      setCancelReason('');
    }
  }, [bookingStatus, showCancelConfirm]);

  if (!event) return null;

  const {
    activity,
    isCompleted,
    dealId,
    dealTitle,
    customerName,
    description,
    conferencingLink,
    bookingCancelToken,
    hostUserName,
    hostAvatarUrl,
    calendarEmail,
    calendarColor,
    calendarOwnerName,
  } = event;

  const isCancelled = description?.startsWith('❌ CANCELADO');
  const canCancel = !isCompleted && !!bookingCancelToken && bookingStatus !== 'cancelled';

  const scheduledDate = new Date(activity.scheduled_at);
  const formattedDate = format(scheduledDate, "EEEE, dd 'de' MMMM 'às' HH:mm", {
    locale: ptBR,
  });

  // Extrai email do título quando vier no formato "Nome (email@dominio)"
  const titleMatch = activity.title.match(/^(.*?)\s*\(([^)]+@[^)]+)\)\s*$/);
  const displayName = titleMatch ? titleMatch[1].trim() : activity.title;
  const displayEmail = titleMatch ? titleMatch[2].trim() : null;

  // Iniciais para fallback do avatar
  const hostInitials = hostUserName
    ? hostUserName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('')
    : '';

  const handleConfirmCancel = () => {
    if (bookingCancelToken && onCancelBooking) {
      onCancelBooking(bookingCancelToken, cancelReason || undefined);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md w-[95vw] md:w-[60vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden">
          {/* ===== Capa premium (padrão notes modal do chat) ===== */}
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3 min-w-0 pr-8">
                <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
                  {TYPE_ICONS_LG[activity.type] ?? <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />}
                </div>
                <div className="flex flex-col min-w-0">
                  <DialogTitle
                    className={cn(
                      'truncate text-base sm:text-lg font-semibold',
                      isCompleted && 'line-through text-muted-foreground',
                    )}
                  >
                    {displayName}
                  </DialogTitle>
                  <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                    {displayEmail ? (
                      <span className="break-all">{displayEmail}</span>
                    ) : (
                      <span className="capitalize">{formattedDate}</span>
                    )}
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-3 justify-end w-full sm:w-auto sm:pr-8">
                {/* Tipo de atividade: ícone colorido + label preto, sem fundo */}
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <span className="text-primary [&_svg]:h-3.5 [&_svg]:w-3.5">
                    {TYPE_ICONS[activity.type]}
                  </span>
                  {ACTIVITY_LABELS[activity.type] || activity.type}
                </span>
                {isCompleted && isCancelled ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
                    <XCircle className="h-3.5 w-3.5 text-destructive" />
                    Cancelado
                  </span>
                ) : isCompleted ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
                    <Check className="h-3.5 w-3.5 text-success" />
                    Concluída
                  </span>
                ) : null}
              </div>
            </div>
          </DialogHeader>

          {/* ===== Corpo rolável ===== */}
          <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-5">
            {/* Chip de data/hora */}
            <div className="flex items-center gap-2 bg-muted/50 border rounded-xl p-3 text-sm font-medium text-foreground/80">
              <Calendar className="h-4 w-4 text-primary shrink-0" />
              <span className="capitalize">{formattedDate}</span>
            </div>

            {/* Descrição */}
            {description && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Descrição
                </p>
                <p
                  className="text-sm leading-relaxed text-foreground/90"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                />
              </div>
            )}

            {/* Link do Google Meet */}
            {conferencingLink && (
              <a
                href={conferencingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3 transition-colors hover:bg-primary/10"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
                  <Video className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Google Meet</p>
                  <p className="text-xs text-muted-foreground">Clique para entrar na reunião</p>
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
              </a>
            )}

            {/* Grid Responsável / Calendário */}
            {(hostUserName || calendarEmail) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {hostUserName && (
                  <div className="space-y-1.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Responsável
                    </p>
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar className="h-7 w-7 shrink-0">
                        {hostAvatarUrl ? <AvatarImage src={hostAvatarUrl} alt={hostUserName} /> : null}
                        <AvatarFallback className="text-[10px] font-bold bg-muted text-muted-foreground">
                          {hostInitials || <User className="h-3.5 w-3.5" />}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground/90 truncate">{hostUserName}</span>
                    </div>
                  </div>
                )}
                {calendarEmail && (
                  <div className="space-y-1.5 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Calendário
                    </p>
                    <div className="flex items-start gap-2 min-w-0">
                      {/* Bolinha de cor do calendário (v1.3.0) */}
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0 border border-border mt-1.5"
                        style={{ backgroundColor: calendarColor || 'hsl(var(--muted-foreground))' }}
                        aria-hidden
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-foreground/90 truncate">{calendarEmail}</span>
                        {/* v1.4.0 — nome do dono para diferenciar "Pessoal" entre agentes */}
                        {calendarOwnerName && calendarOwnerName !== calendarEmail && (
                          <span className="text-xs text-muted-foreground truncate">
                            {calendarOwnerName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Card de contexto: Negócio + Cliente */}
            {(dealTitle || customerName) && (
              <div className="bg-muted/40 border rounded-xl p-4 space-y-3">
                {dealTitle && (
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                        Negócio
                      </p>
                      <p className="text-sm font-semibold text-foreground break-words">{dealTitle}</p>
                    </div>
                  </div>
                )}
                {customerName && (
                  <div
                    className={cn(
                      'flex items-start gap-3',
                      dealTitle && 'border-t pt-3',
                    )}
                  >
                    <User className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                        Cliente
                      </p>
                      <p className="text-sm text-foreground/90 break-words">{customerName}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== Footer fixo: botões secundários + ação principal lado a lado ===== */}
          <div className="border-t px-4 sm:px-6 py-3 flex flex-row gap-2 w-full">
            {dealId && (
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => onOpenDeal(dealId)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Negócio
              </Button>
            )}
            {canCancel && onCancelBooking && (
              <Button
                variant="outline"
                className="flex-1 rounded-xl bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/15 hover:text-destructive"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4 mr-2" />
                )}
                Cancelar
              </Button>
            )}

            {isCompleted ? (
              <Button
                variant="secondary"
                className="flex-1 rounded-xl"
                onClick={() => onReopen(activity.id)}
                disabled={isReopening}
              >
                {isReopening ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Reabrir
              </Button>
            ) : (
              <Button
                className="flex-1 rounded-xl font-semibold bg-success text-success-foreground hover:bg-success/90"
                onClick={() => onComplete(activity.id)}
                disabled={isCompleting}
              >
                {isCompleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Concluir
              </Button>
            )}
          </div>

        </DialogContent>
      </Dialog>

      {/* AlertDialog de confirmação de cancelamento */}
      <AlertDialog
        open={showCancelConfirm}
        onOpenChange={(open) => {
          if (!isCancelling) {
            setShowCancelConfirm(open);
            if (!open) setCancelReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Agendamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar este agendamento? O horário será liberado na agenda e o evento será removido do Google Calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="Motivo do cancelamento (opcional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              disabled={isCancelling}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling} onClick={() => setCancelReason('')}>
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Confirmar Cancelamento'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
